import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiUser, FiClipboard, FiDollarSign, FiCalendar,
    FiArrowRight, FiBookOpen, FiUsers, FiBriefcase,
    FiClock, FiAlertCircle, FiAlertTriangle, FiSave, FiArrowLeft,
    FiLock, FiEdit2, FiEye, FiBarChart2, FiPrinter,
    FiSearch, FiLayers, FiCheckCircle
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import portalService from "../../../services/portalService";
import TeacherLeave from "./TeacherLeave";
import TeacherSyllabusTracker from "./TeacherSyllabusTracker";
import CollegeLogoBadge from "../../../components/common/CollegeLogoBadge";

export { TeacherLeave, TeacherSyllabusTracker };



const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;
const MONTHS_LONG = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const nowT = new Date();

// Safe data extractor — handles { success, data: X } and X directly
const extract = (result) => result?.data ?? result;

// ─────────────────────────────────────────────────────────────────────────────
// TeacherDashboard
// ─────────────────────────────────────────────────────────────────────────────
export function TeacherDashboard() {
    const { user, collegeName } = useApp();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [attendance, setAttendance] = useState(null);
    const [payslips, setPayslips] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            portalService.getTeacherProfile(),
            portalService.getTeacherAttendance(),
            portalService.getTeacherPayslips(),
            portalService.getEvents(),
        ])
            .then(([p, a, py, ev]) => {
                setProfile(extract(p));
                setAttendance(extract(a));
                setPayslips(extract(py) || []);
                setEvents(extract(ev) || []);
            })
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading your dashboard…</div>;

    const attPct = attendance?.summary?.total > 0 ? Math.round((attendance.summary.present / attendance.summary.total) * 100) : 0;
    const attColor = attPct >= 75 ? "#16a34a" : attPct >= 60 ? "#d97706" : "#dc2626";
    const latestSlip = Array.isArray(payslips) ? payslips[0] : null;

    return (
        <div>
            <div className="page-header" style={{ marginBottom: "1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {collegeName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>Welcome, {profile?.fullName?.split(" ")?.[0] || user?.displayName} 👋</h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>{profile?.designation} · {profile?.department}</p>
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginBottom: "1.25rem", padding: "1.25rem", borderLeft: "4px solid #7c3aed" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#7c3aed", flexShrink: 0 }}>
                        {profile?.fullName?.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "T"}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>{profile?.fullName || user?.displayName}</div>
                        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 3 }}>{profile?.employeeCategory} · {profile?.employeeCode}</div>
                    </div>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.85rem", marginBottom: "1.25rem" }}>
                {[
                    { icon: FiClipboard, label: "This Month Att.", to: "/teacher/attendance", color: attColor, value: `${attPct}%` },
                    { icon: FiDollarSign, label: "Latest Net Pay", to: "/teacher/payslips", color: "#16a34a", value: latestSlip ? inr(latestSlip.netPay) : "—" },
                    { icon: FiCalendar, label: "Upcoming Events", to: "/teacher/events", color: "#003b67", value: Array.isArray(events) ? events.length : 0 },
                ].map(({ icon: Icon, label, to, color, value }) => (
                    <div key={to} className="card" style={{ padding: "1rem", borderLeft: `4px solid ${color}`, cursor: "pointer" }}
                        onClick={() => navigate(to)}
                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.1)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "var(--shadow)"; }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 6 }}>
                            <Icon size={13} color={color} />
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginTop: 4 }}>{label}</div>
                    </div>
                ))}
            </div>

            <div className="card" style={{ marginBottom: "1rem" }}>
                <div className="card-header">Quick Access</div>
                <div className="card-body" style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
                    {[
                        { label: "My Leaves", to: "/teacher/leave", icon: FiCalendar },
                        { label: "Syllabus Tracker", to: "/teacher/syllabus", icon: FiLayers },
                        { label: "My Subjects", to: "/teacher/subjects", icon: FiBookOpen },
                        { label: "Mark Attendance", to: "/teacher/attendance/mark", icon: FiClipboard },
                        { label: "Attendance Report", to: "/teacher/attendance/report", icon: FiBarChart2 },
                        { label: "Exams & Results", to: "/teacher/exams", icon: FiEdit2 },
                        { label: "Students", to: "/students", icon: FiUsers },
                        { label: "Timetables", to: "/timetables", icon: FiClock },
                    ].map(({ label, to, icon: Icon }) => (
                        <button key={to} className="btn btn-outline" onClick={() => navigate(to)}
                            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <Icon size={13} /> {label}
                        </button>
                    ))}
                </div>
            </div>


            {Array.isArray(events) && events.length > 0 && (
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}><FiCalendar size={13} /> Upcoming Events</span>
                        <button className="btn btn-outline btn-sm" onClick={() => navigate("/teacher/events")}
                            style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>View All <FiArrowRight size={12} /></button>
                    </div>
                    <div>
                        {events.slice(0, 5).map((ev) => (
                            <div key={ev._id} style={{ padding: "0.55rem 1rem", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                                <div style={{ fontWeight: 600 }}>{ev.title}</div>
                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{ev.date}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherProfile
// ─────────────────────────────────────────────────────────────────────────────
export function TeacherProfile() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        portalService.getTeacherProfile()
            .then((result) => {
                const emp = extract(result);
                if (emp?._id || emp?.employeeCode) setProfile(emp);
                else setError("No employee data returned. Check that your account is linked to an employee record.");
            })
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading profile…</div>;
    if (error) return (
        <div>
            <div className="page-header"><h1 className="page-title">My Profile</h1></div>
            <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "1.25rem", display: "flex", gap: "0.75rem" }}>
                <FiAlertCircle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                <div>
                    <div style={{ fontWeight: 700, color: "#dc2626" }}>Could not load profile</div>
                    <div style={{ fontSize: 13, fontFamily: "monospace", marginTop: 4 }}>{error}</div>
                </div>
            </div>
        </div>
    );
    if (!profile) return null;

    const initials = profile.fullName?.split(" ").filter(Boolean).slice(0, 2).map((p) => p?.[0]?.toUpperCase()).join("") || "T";

    return (
        <div>
            <div className="page-header"><h1 className="page-title">My Profile</h1></div>
            <div className="card" style={{ marginBottom: "1rem", padding: "1.5rem" }}>
                <div style={{ display: "flex", gap: "1.25rem", alignItems: "center" }}>
                    <div style={{ width: 72, height: 72, borderRadius: 18, overflow: "hidden", background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: "#7c3aed", flexShrink: 0 }}>
                        {profile.photo?.url ? <img src={profile.photo.url} alt={profile.fullName} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
                    </div>
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{profile.fullName}</div>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: 6 }}>
                            <span className="badge badge-blue">{profile.employeeCode}</span>
                            <span style={{ background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{profile.status}</span>
                            <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{profile.employeeCategory}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div className="card">
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}><FiBriefcase size={13} /> Employment</div>
                    <div className="card-body">
                        {[["Department", profile.department], ["Designation", profile.designation], ["Qualification", profile.qualification || "—"], ["Experience", profile.experience || "—"], ["Joining Date", profile.joiningDate], ["Date of Birth", profile.dateOfBirth || "—"]].map(([l, v]) => (
                            <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid var(--border)" }}>
                                <span style={{ color: "var(--text-muted)" }}>{l}</span><span style={{ fontWeight: 600 }}>{v}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="card">
                    <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}><FiUser size={13} /> Contact</div>
                    <div className="card-body">
                        {[["Mobile", profile.mobile || "—"], ["Email", profile.email || "—"], ["Address", profile.address || "—"]].map(([l, v]) => (
                            <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid var(--border)" }}>
                                <span style={{ color: "var(--text-muted)" }}>{l}</span><span style={{ fontWeight: 600 }}>{v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherAttendance
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_COLORS_T = {
    Present: { bg: "#dcfce7", color: "#15803d" }, Late: { bg: "#fef9c3", color: "#92400e" },
    "Half Day": { bg: "#fef9c3", color: "#92400e" }, Absent: { bg: "#fee2e2", color: "#dc2626" },
    "On Leave": { bg: "#e8f1f8", color: "#002747" }, "Medical Leave": { bg: "#e8f1f8", color: "#002747" },
};

export function TeacherAttendance() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [month, setMonth] = useState(nowT.getMonth() + 1);
    const [year, setYear] = useState(nowT.getFullYear());
    const yearOpts = Array.from({ length: 3 }, (_, i) => nowT.getFullYear() - i);

    useEffect(() => {
        setLoading(true); setError(null);
        const m = String(month).padStart(2, "0");
        const lastD = new Date(year, month, 0).getDate();
        portalService.getTeacherAttendance({ fromDate: `${year}-${m}-01`, toDate: `${year}-${m}-${String(lastD).padStart(2, "0")}` })
            .then((result) => setData(extract(result)))
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, [month, year]);

    const entries = data?.entries || [];
    const summary = data?.summary || {};
    const pct = summary.total > 0 ? Math.round((summary.present / summary.total) * 100) : 0;
    const color = pct >= 75 ? "#16a34a" : pct >= 60 ? "#d97706" : "#dc2626";

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div><h1 className="page-title">My Attendance</h1></div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <select className="form-control" style={{ width: 120, fontSize: 13 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {MONTHS_LONG.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </select>
                    <select className="form-control" style={{ width: 100, fontSize: 13 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>
            {error && <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#7f1d1d", fontFamily: "monospace" }}>{error}</div>}
            {loading ? <div className="loading-center"><div className="spinner" /></div> : !error && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 240px", gap: "1rem", alignItems: "start" }}>
                    <div className="card">
                        <div className="card-header">{MONTHS_LONG[month]} {year} — Attendance Log</div>
                        {!entries.length ? (
                            <div className="card-body" style={{ color: "var(--text-muted)", fontSize: 13, textAlign: "center" }}>No attendance recorded for this period.</div>
                        ) : (
                            <div className="table-wrapper">
                                <table>
                                    <thead><tr><th>Date</th><th style={{ textAlign: "center" }}>Status</th><th>Remarks</th></tr></thead>
                                    <tbody>
                                        {[...entries].reverse().map((entry) => {
                                            const sc = STATUS_COLORS_T[entry.status] || { bg: "#f3f4f6", color: "#6b7280" };
                                            return (
                                                <tr key={entry.date}>
                                                    <td style={{ fontSize: 13 }}>{entry.date}</td>
                                                    <td style={{ textAlign: "center" }}><span style={{ ...sc, padding: "2px 9px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{entry.status}</span></td>
                                                    <td style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{entry.remarks || "—"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="card" style={{ padding: "1.25rem", textAlign: "center" }}>
                        <svg width={100} height={100} style={{ display: "block", margin: "0 auto 0.75rem" }}>
                            <circle cx={50} cy={50} r={40} fill="none" stroke={`${color}20`} strokeWidth={9} />
                            <circle cx={50} cy={50} r={40} fill="none" stroke={color} strokeWidth={9}
                                strokeDasharray={`${2 * Math.PI * 40}`}
                                strokeDashoffset={`${2 * Math.PI * 40 * (1 - pct / 100)}`}
                                strokeLinecap="round" transform="rotate(-90 50 50)" />
                            <text x={50} y={46} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 18, fontWeight: 900, fill: color }}>{pct}%</text>
                            <text x={50} y={61} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 9, fill: "#94a3b8" }}>this month</text>
                        </svg>
                        {[["Present", summary.present ?? 0, "#16a34a"], ["Absent", summary.absent ?? 0, "#dc2626"], ["Leave", summary.leave ?? 0, "#003b67"], ["Total", summary.total ?? 0, "#6b7280"]].map(([l, v, c]) => (
                            <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--border)" }}>
                                <span style={{ color: "var(--text-muted)" }}>{l}</span>
                                <span style={{ fontWeight: 700, color: c }}>{v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherMarkAttendance — subject/lecture-level attendance FOR the teacher's
// students, separate from TeacherAttendance above (which is the teacher's
// OWN attendance, marked by admin). Keyed by subject + date + period, so a
// subject meeting twice in one day gets two independently-markable entries.
// ─────────────────────────────────────────────────────────────────────────────
const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const PRESENT_LIKE_T = new Set(["Present", "Late", "Half Day"]);

export function TeacherMarkAttendance() {
    const { academicYear } = useApp();
    const navigate = useNavigate();

    const [subjects, setSubjects] = useState([]);
    const [subjectId, setSubjectId] = useState("");
    const [attendanceDate, setAttendanceDate] = useState(todayStr());
    const [periods, setPeriods] = useState([]);
    const [periodNo, setPeriodNo] = useState("");
    const [sheet, setSheet] = useState(null);
    const [rows, setRows] = useState([]);
    const [notes, setNotes] = useState("");

    const [loadingSubjects, setLoadingSubjects] = useState(true);
    const [loadingPeriods, setLoadingPeriods] = useState(false);
    const [loadingSheet, setLoadingSheet] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        portalService.getTeacherSubjects()
            .then((result) => setSubjects(extract(result) || []))
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoadingSubjects(false));
    }, []);

    const fetchPeriods = useCallback(() => {
        if (!subjectId || !attendanceDate || !academicYear?._id) return;
        setLoadingPeriods(true); setError(null);
        setPeriods([]); setPeriodNo(""); setSheet(null); setRows([]);
        portalService.getTeacherSubjectPeriods({ subjectId, academicYear: academicYear._id, attendanceDate })
            .then((result) => {
                const list = extract(result);
                setPeriods(Array.isArray(list) ? list : []);
            })
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoadingPeriods(false));
    }, [subjectId, attendanceDate, academicYear]);

    useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

    const fetchSheet = useCallback(() => {
        if (!subjectId || !attendanceDate || !periodNo || !academicYear?._id) return;
        setLoadingSheet(true); setError(null);
        portalService.getTeacherSubjectSheet({ subjectId, academicYear: academicYear._id, attendanceDate, periodNo })
            .then((result) => {
                const fullSheet = result?.data !== undefined ? result : { data: result };
                setSheet(fullSheet);
                const students = Array.isArray(result) ? result : (result?.data || []);
                setRows(students.map((s) => ({
                    studentId: s._id,
                    fullName: s.fullName,
                    rollNo: s.rollNo,
                    enrollmentNo: s.enrollmentNo,
                    dailyStatus: s.dailyStatus,
                    status: s.attendance?.status || "",
                    remarks: s.attendance?.remarks || "",
                })));
                setNotes(fullSheet?.meta?.notes || result?.meta?.notes || "");
            })
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoadingSheet(false));
    }, [subjectId, attendanceDate, periodNo, academicYear]);

    useEffect(() => { fetchSheet(); }, [fetchSheet]);

    const statusOptions = sheet?.meta?.statusOptions || sheet?.statusOptions || ["Present", "Absent", "Late", "On Leave", "Medical Leave", "Half Day"];

    const updateRow = (studentId, field, value) => {
        setRows((prev) => prev.map((r) => (r.studentId === studentId ? { ...r, [field]: value } : r)));
    };
    const markAll = (status) => setRows((prev) => prev.map((r) => ({ ...r, status })));

    const handleSave = async () => {
        const records = rows.filter((r) => r.status).map((r) => ({ student: r.studentId, status: r.status, remarks: r.remarks }));
        if (records.length === 0) { toast.error("Mark at least one student before saving."); return; }
        setSaving(true);
        try {
            const res = await portalService.markTeacherSubjectAttendance({
                subjectId, academicYear: academicYear._id, attendanceDate, periodNo: Number(periodNo),
                notes, records,
            });
            toast.success(res.message || "Attendance saved.");
            fetchSheet();
            fetchPeriods(); // refresh "already marked" flags
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!academicYear?._id) return (
        <div>
            <div className="page-header"><h1 className="page-title">Mark Attendance</h1></div>
            <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>Select an academic year from the Dashboard first.</div></div>
        </div>
    );

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">Mark Attendance</h1>
                    <p className="page-subtitle">Lecture-wise attendance for your assigned subjects · {academicYear.label}</p>
                </div>
                <button
                    className="btn btn-outline"
                    onClick={() => navigate("/teacher/attendance/report")}
                    style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                >
                    <FiBarChart2 size={13} /> View Attendance Report
                </button>
            </div>

            {error && <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#7f1d1d" }}>{error}</div>}

            <div className="card" style={{ marginBottom: "1rem" }}>
                <div className="card-body" style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end" }}>
                    <div style={{ minWidth: 220 }}>
                        <label className="form-label">Subject</label>
                        <select className="form-control" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} disabled={loadingSubjects}>
                            <option value="">{loadingSubjects ? "Loading…" : "Select subject"}</option>
                            {subjects.map((s) => {
                                const courseText = s.course?.shortName || s.course?.courseName || "";
                                const yearText = s.yearNumber ? `${s.yearNumber}${s.yearNumber === 1 ? "st" : s.yearNumber === 2 ? "nd" : s.yearNumber === 3 ? "rd" : "th"} Year` : "";
                                const classLabel = [courseText, yearText].filter(Boolean).join(" — ");
                                return (
                                    <option key={s._id} value={s._id}>
                                        {s.name}{classLabel ? ` (${classLabel})` : ""}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    <div>
                        <label className="form-label">Date</label>
                        <input type="date" className="form-control" value={attendanceDate} max={todayStr()}
                            onChange={(e) => setAttendanceDate(e.target.value)} />
                    </div>
                    <div style={{ minWidth: 220 }}>
                        <label className="form-label">Lecture Period</label>
                        <select className="form-control" value={periodNo} onChange={(e) => setPeriodNo(e.target.value)}
                            disabled={!subjectId || loadingPeriods || periods.length === 0}>
                            <option value="">
                                {loadingPeriods ? "Loading…" : periods.length === 0 ? "No periods scheduled" : "Select period"}
                            </option>
                            {periods.map((p) => (
                                <option key={p.periodNo} value={p.periodNo}>
                                    Period {p.periodNo} · {p.startTime}–{p.endTime}{p.alreadyMarked ? " (already marked)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {loadingSheet ? (
                <div className="loading-center"><div className="spinner" />Loading roster…</div>
            ) : !sheet ? (
                <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                    Pick a subject, date and period to load the class roster.
                </div></div>
            ) : rows.length === 0 ? (
                <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                    No students found for this subject's course and year.
                </div></div>
            ) : (
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                        <span>{rows.length} student{rows.length !== 1 ? "s" : ""}</span>
                        <div style={{ display: "flex", gap: "0.4rem" }}>
                            <button className="btn btn-outline btn-sm" onClick={() => markAll("Present")}>Mark all Present</button>
                            <button className="btn btn-outline btn-sm" onClick={() => markAll("Absent")}>Mark all Absent</button>
                        </div>
                    </div>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th style={{ textAlign: "center" }}>Daily Status</th>
                                    <th style={{ textAlign: "center" }}>Lecture Status</th>
                                    <th>Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const dsc = STATUS_COLORS_T[row.dailyStatus] || { bg: "#f3f4f6", color: "#6b7280" };
                                    const mismatch = row.dailyStatus && row.status &&
                                        (PRESENT_LIKE_T.has(row.dailyStatus) !== PRESENT_LIKE_T.has(row.status));
                                    return (
                                        <tr key={row.studentId} style={mismatch ? { background: "#fffbeb" } : undefined}>
                                            <td>
                                                <div style={{ fontWeight: 600, fontSize: 13 }}>{row.fullName}</div>
                                                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{row.rollNo || row.enrollmentNo}</div>
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {row.dailyStatus ? (
                                                    <span style={{ ...dsc, padding: "2px 9px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{row.dailyStatus}</span>
                                                ) : (
                                                    <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>Not marked</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <select className="form-control" style={{ width: 150, display: "inline-block", fontSize: 12.5 }}
                                                    value={row.status} onChange={(e) => updateRow(row.studentId, "status", e.target.value)}>
                                                    <option value="">— Not marked —</option>
                                                    {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                {mismatch && <div style={{ fontSize: 10.5, color: "#92400e", marginTop: 3 }}>⚠ differs from daily</div>}
                                            </td>
                                            <td>
                                                <input className="form-control" style={{ fontSize: 12.5 }}
                                                    value={row.remarks} onChange={(e) => updateRow(row.studentId, "remarks", e.target.value)}
                                                    placeholder="Optional" />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="card-body" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                        <input className="form-control" style={{ maxWidth: 320, fontSize: 12.5 }}
                            value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Lecture notes (optional)" />
                        <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
                            <FiSave size={13} /> {saving ? "Saving…" : "Save Attendance"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherSubjectAttendanceReport — analytics and breakdown for teacher's subjects
// ─────────────────────────────────────────────────────────────────────────────
export function TeacherSubjectAttendanceReport() {
    const { academicYear } = useApp();
    const navigate = useNavigate();

    const [subjects, setSubjects] = useState([]);
    const [subjectId, setSubjectId] = useState("");
    const [reportType, setReportType] = useState("monthly");
    const [month, setMonth] = useState(nowT.getMonth() + 1);
    const [year, setYear] = useState(nowT.getFullYear());
    const yearOpts = Array.from({ length: 3 }, (_, i) => nowT.getFullYear() - i);
    const [search, setSearch] = useState("");
    const [selectedStudentId, setSelectedStudentId] = useState("");
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        portalService.getTeacherSubjects()
            .then((result) => {
                const list = extract(result) || [];
                setSubjects(list);
            })
            .catch((err) => setError(err.response?.data?.message || err.message));
    }, []);

    const fetchReport = useCallback(() => {
        if (!academicYear?._id) return;
        setLoading(true);
        setError(null);
        portalService.getTeacherSubjectAttendanceReport({
            academicYear: academicYear._id,
            subjectId: subjectId || undefined,
            reportType,
            month,
            year,
            search,
            studentId: selectedStudentId || undefined,
        })
            .then((result) => setReportData(extract(result)))
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, [academicYear, subjectId, reportType, month, year, search, selectedStudentId]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handlePrint = () => {
        window.print();
    };

    if (!academicYear?._id) return (
        <div>
            <div className="page-header"><h1 className="page-title">Subject Attendance Report</h1></div>
            <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>Select an academic year from the Dashboard first.</div></div>
        </div>
    );

    const individualReport = reportData?.individualStudentReport;
    const studentSummaries = reportData?.studentSummaries || [];
    const summary = reportData?.summary || {};
    const range = reportData?.range || {};

    return (
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            {/* Header */}
            <div className="page-header no-print" style={{ flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
                <div>
                    <h1 className="page-title">Subject Attendance Report</h1>
                    <p className="page-subtitle">Detailed attendance summaries for your assigned subjects · {academicYear.label}</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {selectedStudentId && (
                        <button className="btn btn-outline" onClick={() => setSelectedStudentId("")} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiArrowLeft size={14} /> Back to Roster
                        </button>
                    )}
                    <button className="btn btn-outline" onClick={() => navigate("/teacher/attendance/mark")} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiClipboard size={14} /> Mark Attendance
                    </button>
                    <button className="btn btn-primary" onClick={handlePrint} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiPrinter size={14} /> Print Report
                    </button>
                </div>
            </div>

            {error && <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#7f1d1d" }}>{error}</div>}

            {/* Filter Bar */}
            <div className="card no-print" style={{ marginBottom: "1.25rem", padding: "1rem" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem", alignItems: "flex-end" }}>
                    <div>
                        <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Subject</label>
                        <select className="form-control" style={{ fontSize: 13 }} value={subjectId} onChange={(e) => { setSubjectId(e.target.value); setSelectedStudentId(""); }}>
                            <option value="">All My Assigned Subjects</option>
                            {subjects.map((s) => {
                                const courseText = s.course?.shortName || s.course?.courseName || "";
                                const yearText = s.yearNumber ? `${s.yearNumber} Yr` : "";
                                const label = [courseText, yearText].filter(Boolean).join(" · ");
                                return <option key={s._id} value={s._id}>{s.name} {label ? `(${label})` : ""}</option>;
                            })}
                        </select>
                    </div>

                    <div>
                        <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Timeframe</label>
                        <select className="form-control" style={{ fontSize: 13 }} value={reportType} onChange={(e) => setReportType(e.target.value)}>
                            <option value="monthly">Monthly</option>
                            <option value="weekly">Weekly</option>
                            <option value="yearly">Yearly</option>
                        </select>
                    </div>

                    {reportType === "monthly" && (
                        <div>
                            <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Month & Year</label>
                            <div style={{ display: "flex", gap: "0.35rem" }}>
                                <select className="form-control" style={{ fontSize: 13 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                                    {MONTHS_LONG.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                                </select>
                                <select className="form-control" style={{ width: 85, fontSize: 13 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                                    {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="form-label" style={{ fontSize: 12, fontWeight: 600 }}>Search Student</label>
                        <div style={{ position: "relative" }}>
                            <input
                                type="text"
                                className="form-control"
                                style={{ paddingLeft: "2rem", fontSize: 13 }}
                                placeholder="Name / Roll / Enrollment"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <FiSearch size={14} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-center"><div className="spinner" /> Loading report…</div>
            ) : individualReport ? (
                /* Individual Student View */
                <div>
                    <div className="card" style={{ marginBottom: "1rem", padding: "1.25rem 1.5rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
                            <div style={{ display: "flex", gap: "1.25rem", alignItems: "center" }}>
                                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#e8f1f8", color: "#003b67", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800 }}>
                                    {individualReport.student.fullName?.charAt(0) || "S"}
                                </div>
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{individualReport.student.fullName}</h2>
                                        <span className="badge badge-blue">Roll #{individualReport.student.rollNo || "—"}</span>
                                    </div>
                                    <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 3 }}>
                                        Enrollment: {individualReport.student.enrollmentNo || "—"} · Period: {range.fromDate} to {range.toDate}
                                    </div>
                                </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)" }}>Attendance Rate</div>
                                <div style={{ fontSize: 24, fontWeight: 900, color: individualReport.overall.percentage >= 75 ? "#16a34a" : "#dc2626" }}>
                                    {individualReport.overall.percentage}%
                                </div>
                                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                    {individualReport.overall.attended} / {individualReport.overall.totalLectures} lectures
                                </div>
                            </div>
                        </div>

                        {individualReport.overall.isLowAttendance && (
                            <div style={{ marginTop: "1rem", padding: "0.6rem 0.85rem", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#991b1b", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                                <FiAlertTriangle size={15} /> Short Attendance Alert: Below mandatory 75% attendance.
                            </div>
                        )}
                    </div>

                    {/* Subject Table */}
                    <div className="card" style={{ marginBottom: "1.25rem" }}>
                        <div className="card-header" style={{ fontWeight: 700 }}><FiLayers size={14} /> Subject Breakdown</div>
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Subject</th>
                                        <th>Type</th>
                                        <th style={{ textAlign: "center" }}>Held</th>
                                        <th style={{ textAlign: "center" }}>Attended</th>
                                        <th style={{ textAlign: "center" }}>Absent</th>
                                        <th style={{ textAlign: "center" }}>Late</th>
                                        <th style={{ minWidth: 140 }}>Rate</th>
                                        <th style={{ textAlign: "center" }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {individualReport.bySubject.map((s, idx) => {
                                        const pctColor = s.percentage >= 75 ? "#16a34a" : s.percentage >= 60 ? "#d97706" : "#dc2626";
                                        return (
                                            <tr key={s.subjectId || idx}>
                                                <td style={{ fontWeight: 600, fontSize: 13 }}>{s.subjectName}</td>
                                                <td style={{ fontSize: 12 }}>{s.type}</td>
                                                <td style={{ textAlign: "center", fontWeight: 600 }}>{s.totalLectures}</td>
                                                <td style={{ textAlign: "center", color: "#16a34a", fontWeight: 700 }}>{s.attended}</td>
                                                <td style={{ textAlign: "center", color: "#dc2626" }}>{s.absent}</td>
                                                <td style={{ textAlign: "center", color: "#d97706" }}>{s.late}</td>
                                                <td>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                        <div style={{ flex: 1, background: "#e2e8f0", height: 6, borderRadius: 99, overflow: "hidden" }}>
                                                            <div style={{ width: `${Math.min(s.percentage, 100)}%`, background: pctColor, height: "100%" }} />
                                                        </div>
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: pctColor, minWidth: 38, textAlign: "right" }}>{s.percentage}%</span>
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    {s.isLowAttendance ? (
                                                        <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>Short (&lt;75%)</span>
                                                    ) : (
                                                        <span style={{ background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>Good</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Lecture Log */}
                    <div className="card">
                        <div className="card-header" style={{ fontWeight: 700 }}><FiClock size={14} /> Lecture History ({individualReport.lectureLog.length})</div>
                        {individualReport.lectureLog.length === 0 ? (
                            <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "1.5rem" }}>No lecture sessions recorded for this period.</div>
                        ) : (
                            <div className="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Period</th>
                                            <th>Subject</th>
                                            <th style={{ textAlign: "center" }}>Status</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {individualReport.lectureLog.map((log, idx) => {
                                            const sc = STATUS_COLORS_T[log.status] || { bg: "#f3f4f6", color: "#6b7280" };
                                            return (
                                                <tr key={idx}>
                                                    <td style={{ fontWeight: 600, fontSize: 13 }}>{log.attendanceDate}</td>
                                                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>Period #{log.periodNo} {log.startTime ? `(${log.startTime}–${log.endTime})` : ""}</td>
                                                    <td style={{ fontWeight: 600, fontSize: 13 }}>{log.subjectName}</td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <span style={{ ...sc, padding: "2px 9px", borderRadius: 99, fontSize: 11.5, fontWeight: 700 }}>{log.status}</span>
                                                    </td>
                                                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{log.remarks || "—"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* Class Roster Summary */
                <div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                        <div className="card" style={{ padding: "1rem" }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Lectures Held</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: "var(--primary)", marginTop: 4 }}>{summary.lecturesHeld || 0}</div>
                        </div>
                        <div className="card" style={{ padding: "1rem" }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Total Students</div>
                            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{summary.totalStudents || 0}</div>
                        </div>
                        <div className="card" style={{ padding: "1rem" }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Average Attendance Rate</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: summary.presentPercentage >= 75 ? "#16a34a" : "#dc2626", marginTop: 4 }}>{summary.presentPercentage || 0}%</div>
                        </div>
                        <div className="card" style={{ padding: "1rem" }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>Students &lt; 75% Attendance</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: summary.lowAttendanceCount > 0 ? "#dc2626" : "#16a34a", marginTop: 4 }}>{summary.lowAttendanceCount || 0}</div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontWeight: 700 }}><FiUser size={14} /> Student Attendance Roster ({studentSummaries.length})</span>
                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Click any student to see detailed breakdown</span>
                        </div>

                        {studentSummaries.length === 0 ? (
                            <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                                No attendance records found for this period.
                            </div>
                        ) : (
                            <div className="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Roll No</th>
                                            <th>Student Name</th>
                                            <th>Enrollment No</th>
                                            <th style={{ textAlign: "center" }}>Held</th>
                                            <th style={{ textAlign: "center" }}>Attended</th>
                                            <th style={{ textAlign: "center" }}>Absent</th>
                                            <th style={{ minWidth: 120 }}>Attendance Rate</th>
                                            <th style={{ textAlign: "right" }} className="no-print">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {studentSummaries.map((entry) => {
                                            const s = entry.student;
                                            const pct = entry.attendancePercentage;
                                            const pctColor = pct >= 75 ? "#16a34a" : pct >= 60 ? "#d97706" : "#dc2626";
                                            return (
                                                <tr key={s._id} style={{ cursor: "pointer" }} onClick={() => setSelectedStudentId(s._id)}>
                                                    <td style={{ fontWeight: 600 }}>{s.rollNo || "—"}</td>
                                                    <td>
                                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{s.fullName}</div>
                                                    </td>
                                                    <td><span className="badge badge-blue">{s.enrollmentNo || "—"}</span></td>
                                                    <td style={{ textAlign: "center", fontWeight: 600 }}>{entry.totalLectures}</td>
                                                    <td style={{ textAlign: "center", color: "#16a34a", fontWeight: 700 }}>{entry.presentCount}</td>
                                                    <td style={{ textAlign: "center", color: "#dc2626" }}>{entry.absentCount}</td>
                                                    <td>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                            <div style={{ flex: 1, background: "#e2e8f0", height: 6, borderRadius: 99, overflow: "hidden" }}>
                                                                <div style={{ width: `${Math.min(pct, 100)}%`, background: pctColor, height: "100%" }} />
                                                            </div>
                                                            <span style={{ fontSize: 12, fontWeight: 700, color: pctColor, minWidth: 38, textAlign: "right" }}>{pct}%</span>
                                                        </div>
                                                    </td>
                                                    <td style={{ textAlign: "right" }} className="no-print">
                                                        <button className="btn btn-outline btn-sm" onClick={(e) => { e.stopPropagation(); setSelectedStudentId(s._id); }} style={{ fontSize: 11.5 }}>
                                                            View Breakdown →
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherPayslips
// ─────────────────────────────────────────────────────────────────────────────
export function TeacherPayslips() {
    const [payslips, setPayslips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        portalService.getTeacherPayslips()
            .then((result) => setPayslips(Array.isArray(extract(result)) ? extract(result) : []))
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="loading-center"><div className="spinner" /></div>;
    return (
        <div>
            <div className="page-header"><h1 className="page-title">My Payslips</h1></div>
            {error && <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#7f1d1d", fontFamily: "monospace" }}>{error}</div>}
            {!error && payslips.length === 0 && (
                <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>No finalized payslips yet. Payslips appear here once admin finalizes the monthly payroll.</div></div>
            )}
            {payslips.length > 0 && (
                <div className="card">
                    <div className="table-wrapper">
                        <table>
                            <thead><tr><th>Month / Year</th><th style={{ textAlign: "center" }}>Working Days</th><th style={{ textAlign: "center" }}>Present</th><th style={{ textAlign: "center" }}>LOP Days</th><th style={{ textAlign: "right" }}>Gross</th><th style={{ textAlign: "right" }}>Deductions</th><th style={{ textAlign: "right" }}>Net Pay</th><th style={{ textAlign: "right" }}>Payslip</th></tr></thead>
                            <tbody>
                                {payslips.map((p) => (
                                    <tr key={p._id}>
                                        <td style={{ fontWeight: 600 }}>{MONTHS_LONG[p.month]} {p.year}</td>
                                        <td style={{ textAlign: "center", fontSize: 13 }}>{p.workingDays}</td>
                                        <td style={{ textAlign: "center", fontSize: 13, color: "#16a34a", fontWeight: 600 }}>{p.presentDays}</td>
                                        <td style={{ textAlign: "center", fontSize: 13, color: "#dc2626" }}>{p.lopDays}</td>
                                        <td style={{ textAlign: "right", fontSize: 13 }}>{inr(p.grossPay)}</td>
                                        <td style={{ textAlign: "right", fontSize: 13, color: "#dc2626" }}>{inr(p.totalDeductions)}</td>
                                        <td style={{ textAlign: "right", fontWeight: 700, color: "#16a34a" }}>{inr(p.netPay)}</td>
                                        <td style={{ textAlign: "right" }}><button className="btn btn-outline btn-sm" onClick={() => navigate(`/payroll/${p._id}`)}>View</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherEvents
// ─────────────────────────────────────────────────────────────────────────────
const EVENT_CFG = { Holiday: { color: "#dc2626", bg: "#fee2e2" }, Exam: { color: "#7c3aed", bg: "#ede9fe" }, Event: { color: "#003b67", bg: "#e8f1f8" }, Meeting: { color: "#d97706", bg: "#fef3c7" }, Other: { color: "#6b7280", bg: "#f3f4f6" } };

export function TeacherEvents() {
    const [events, setEvents] = useState([]);
    const [month, setMonth] = useState(nowT.getMonth() + 1);
    const [year, setYear] = useState(nowT.getFullYear());
    const [loading, setLoading] = useState(true);
    const yearOpts = Array.from({ length: 3 }, (_, i) => nowT.getFullYear() - i);

    useEffect(() => {
        setLoading(true);
        portalService.getEvents({ month, year })
            .then((result) => setEvents(extract(result) || []))
            .catch((err) => toast.error(err.message))
            .finally(() => setLoading(false));
    }, [month, year]);

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div><h1 className="page-title">Events & Calendar</h1></div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <select className="form-control" style={{ width: 130, fontSize: 13 }} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {MONTHS_LONG.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </select>
                    <select className="form-control" style={{ width: 100, fontSize: 13 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>
            {loading ? <div className="loading-center"><div className="spinner" /></div> : !Array.isArray(events) || events.length === 0 ? (
                <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>No events in {MONTHS_LONG[month]} {year}.</div></div>
            ) : (
                <div style={{ display: "grid", gap: "0.65rem" }}>
                    {events.map((ev) => {
                        const ec = EVENT_CFG[ev.type] || EVENT_CFG.Other;
                        return (
                            <div key={ev._id} className="card" style={{ padding: "0.9rem 1.1rem", display: "flex", gap: "0.75rem", alignItems: "flex-start", borderLeft: `4px solid ${ec.color}` }}>
                                <div style={{ width: 32, height: 32, borderRadius: 9, background: ec.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <span style={{ fontSize: 14 }}>{ev.type === "Holiday" ? "🏖" : ev.type === "Meeting" ? "👥" : ev.type === "Exam" ? "📝" : "📅"}</span>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{ev.title}</div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                                        {ev.date}{ev.endDate && ev.endDate !== ev.date ? ` → ${ev.endDate}` : ""} · <span style={{ color: ec.color, fontWeight: 600 }}>{ev.type}</span>
                                        {ev.isHoliday && <span style={{ marginLeft: 6, fontSize: 10, background: "#fee2e2", color: "#dc2626", padding: "1px 5px", borderRadius: 99, fontWeight: 700 }}>No Classes</span>}
                                    </div>
                                    {ev.description && <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>{ev.description}</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherSubjects — all subjects assigned to this teacher
// ─────────────────────────────────────────────────────────────────────────────
const COURSE_COLORS = { "D. PHARM": "#003b67", "B. PHARM": "#059669", "M. PHARM": "#7c3aed" };
const TYPE_STYLES = { Theory: { bg: "#e8f1f8", color: "#002747" }, Practical: { bg: "#dcfce7", color: "#15803d" }, Elective: { bg: "#fef9c3", color: "#92400e" }, Project: { bg: "#fce7f3", color: "#9d174d" }, Other: { bg: "#f3f4f6", color: "#6b7280" } };

export function TeacherSubjects() {
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        portalService.getTeacherSubjects()
            .then((result) => setSubjects(extract(result) || []))
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    // Group by course+year
    const groups = subjects.reduce((acc, sub) => {
        const courseText = sub.course?.shortName || sub.course?.courseName || sub.courseAdmitted || "Course";
        const yearText = sub.courseYear || (sub.yearNumber ? `${sub.yearNumber} Year` : "");
        const key = [courseText, yearText].filter(Boolean).join(" — ") || "Other";
        if (!acc[key]) acc[key] = [];
        acc[key].push(sub);
        return acc;
    }, {});

    if (loading) return <div className="loading-center"><div className="spinner" />Loading subjects…</div>;

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">My Subjects</h1>
                    <p className="page-subtitle">{subjects.length} subject{subjects.length !== 1 ? "s" : ""} assigned</p>
                </div>
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate("/teacher/syllabus")}
                    style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                >
                    <FiLayers size={13} /> Syllabus Tracker
                </button>
            </div>

            {error && (
                <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#7f1d1d" }}>{error}</div>
            )}

            {subjects.length === 0 && !error ? (
                <div className="card">
                    <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                        <FiBookOpen size={40} style={{ opacity: 0.2, display: "block", margin: "0 auto 0.75rem" }} />
                        <p>No subjects assigned yet. Contact admin to assign subjects to your account.</p>
                    </div>
                </div>
            ) : (
                <div style={{ display: "grid", gap: "1rem" }}>
                    {Object.entries(groups).map(([groupKey, subs]) => {
                        const color = COURSE_COLORS[subs[0]?.courseAdmitted] || "#6b7280";
                        return (
                            <div key={groupKey} className="card">
                                <div className="card-header" style={{ borderLeft: `4px solid ${color}`, paddingLeft: "0.9rem" }}>
                                    {groupKey}
                                    <span style={{ marginLeft: 8, fontSize: 11, background: color, color: "#fff", padding: "1px 7px", borderRadius: 99, fontWeight: 700 }}>{subs.length}</span>
                                </div>
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Subject</th>
                                                <th>Code</th>
                                                <th style={{ textAlign: "center" }}>Type</th>
                                                <th style={{ textAlign: "center" }}>Credits</th>
                                                <th style={{ textAlign: "center" }}>Hrs/Week</th>
                                                <th style={{ textAlign: "right" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {subs.map((sub, i) => {
                                                const ts = TYPE_STYLES[sub.type] || TYPE_STYLES.Other;
                                                return (
                                                    <tr key={sub._id}>
                                                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{i + 1}</td>
                                                        <td>
                                                            <div style={{ fontWeight: 600, fontSize: 13 }}>{sub.name}</div>
                                                            {sub.description && <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{sub.description}</div>}
                                                        </td>
                                                        <td style={{ fontSize: 12, fontFamily: "monospace" }}>{sub.code || "—"}</td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <span style={{ ...ts, padding: "2px 9px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{sub.type}</span>
                                                        </td>
                                                        <td style={{ textAlign: "center", fontSize: 13 }}>{sub.creditHours || "—"}</td>
                                                        <td style={{ textAlign: "center", fontSize: 13 }}>{sub.weeklyHours || "—"}</td>
                                                        <td style={{ textAlign: "right" }}>
                                                            <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                                <button className="btn btn-outline btn-sm"
                                                                    title="Track syllabus units"
                                                                    onClick={() => navigate("/teacher/syllabus")}
                                                                    style={{ display: "flex", alignItems: "center", gap: "0.3rem", color: "#7c3aed", borderColor: "#7c3aed" }}>
                                                                    <FiLayers size={12} /> Syllabus
                                                                </button>
                                                                <button className="btn btn-outline btn-sm"
                                                                    title="View subject details"
                                                                    onClick={() => navigate(`/subjects/${sub._id}`)}
                                                                    style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                                                                    <FiEye size={12} /> View
                                                                </button>
                                                                <button className="btn btn-outline btn-sm"
                                                                    title="Edit subject"
                                                                    onClick={() => navigate(`/subjects/${sub._id}/edit`)}
                                                                    style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                                                                    <FiEdit2 size={12} /> Edit
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherExams — exams containing at least one of teacher's subjects
// ─────────────────────────────────────────────────────────────────────────────
const EXAM_TYPE_COLORS = { Internal: { bg: "#e8f1f8", color: "#002747" }, External: { bg: "#ede9fe", color: "#7c3aed" }, Practical: { bg: "#dcfce7", color: "#15803d" }, Sessional: { bg: "#fef9c3", color: "#92400e" }, University: { bg: "#fee2e2", color: "#dc2626" }, Other: { bg: "#f3f4f6", color: "#6b7280" } };

export function TeacherExams() {
    const { academicYear } = useApp();
    const navigate = useNavigate();
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchExams = useCallback(() => {
        if (!academicYear?._id) { setLoading(false); return; }
        setLoading(true); setError(null);
        portalService.getTeacherExams({ academicYear: academicYear._id })
            .then((result) => setExams(extract(result) || []))
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, [academicYear]);

    useEffect(() => { fetchExams(); }, [fetchExams]);

    if (!academicYear?._id) return (
        <div>
            <div className="page-header"><h1 className="page-title">Exams & Results</h1></div>
            <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>Select an academic year from the Dashboard to view exams.</div></div>
        </div>
    );

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Exams & Results</h1>
                    <p className="page-subtitle">{academicYear.label} · {exams.length} exam{exams.length !== 1 ? "s" : ""} with your subjects</p>
                </div>
            </div>

            {error && <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#7f1d1d" }}>{error}</div>}

            {loading ? (
                <div className="loading-center"><div className="spinner" /></div>
            ) : exams.length === 0 ? (
                <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>No exams found containing your assigned subjects for {academicYear.label}.</div></div>
            ) : (
                <div style={{ display: "grid", gap: "0.75rem" }}>
                    {exams.map((exam) => {
                        const tc = EXAM_TYPE_COLORS[exam.examType] || EXAM_TYPE_COLORS.Other;
                        const courseLabel = exam.course?.shortName || exam.course?.courseName || "Course";
                        const yearLabel = exam.yearNumber ? `${exam.yearNumber} Year` : "";
                        const classDisplay = [courseLabel, yearLabel].filter(Boolean).join(" · ");
                        const color = COURSE_COLORS[courseLabel] || "#6b7280";
                        return (
                            <div key={exam._id} className="card" style={{ padding: "1rem 1.25rem", borderLeft: `4px solid ${tc.color}` }}>
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: 4 }}>
                                            <span style={{ fontWeight: 700, fontSize: 14 }}>{exam.title}</span>
                                            <span style={{ ...tc, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{exam.examType}</span>
                                            <span style={{ background: `${color}18`, color, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{classDisplay}</span>
                                        </div>
                                        {exam.examDate && <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 6 }}>📅 {exam.examDate}</div>}
                                        {/* My subjects highlight */}
                                        {exam.mySubjects?.length > 0 && (
                                            <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", marginBottom: 6 }}>
                                                <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>Your subjects:</span>
                                                {exam.mySubjects.map((s) => (
                                                    <span key={s} style={{ fontSize: 11.5, background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>{s}</span>
                                                ))}
                                            </div>
                                        )}
                                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                            {exam.resultCount > 0 ? `${exam.resultCount} result${exam.resultCount !== 1 ? "s" : ""} entered` : "No results yet"}
                                            {exam.passCount > 0 && ` · ${exam.passCount} passed`}
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", gap: "0.4rem", flexShrink: 0 }}>
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => navigate(`/exams/${exam._id}/results/entry`)}
                                            style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                                        >
                                            <FiEdit2 size={12} /> Enter Marks
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherResultEntry — enter marks for teacher's subjects only
// ─────────────────────────────────────────────────────────────────────────────
const GRADE_COLORS = { O: "#15803d", "A+": "#002747", A: "#003b67", "B+": "#92400e", B: "#d97706", F: "#dc2626", Ab: "#6b7280", "–": "#9ca3af" };

export function TeacherResultEntry() {
    const { examId } = useParams();
    const navigate = useNavigate();

    const [sheet, setSheet] = useState(null);  // { exam, teacherSubjects, data }
    const [rows, setRows] = useState([]);    // local editable state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        portalService.getTeacherResultSheet(examId)
            .then((result) => {
                setSheet(result);
                // Initialise editable rows
                const students = result.data || [];
                setRows(students.map((s) => ({
                    studentId: s.studentId,
                    fullName: s.fullName,
                    enrollmentNo: s.enrollmentNo,
                    rollNo: s.rollNo,
                    remarks: s.remarks || "",
                    approvalStatus: s.approvalStatus || "Pending",
                    marks: (result.exam?.subjects || []).map((sub) => {
                        const existing = s.marks?.find((m) => m.subjectName === sub.name);
                        return {
                            subjectName: sub.name,
                            maxMarks: sub.maxMarks,
                            passingMarks: sub.passingMarks,
                            marksObtained: existing?.marksObtained ?? 0,
                            isAbsent: existing?.isAbsent ?? false,
                        };
                    }),
                    // Computed from last save
                    totalObtained: s.totalObtained,
                    percentage: s.percentage,
                    grade: s.grade,
                    result: s.result,
                })));
            })
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, [examId]);

    const teacherSet = new Set(sheet?.teacherSubjects || []);
    const exam = sheet?.exam;
    const isExamPublished = exam?.status === "Results Published";
    const approvedCount = rows.filter((r) => r.approvalStatus === "Approved").length;
    const allApproved = rows.length > 0 && rows.every((r) => r.approvalStatus === "Approved" || isExamPublished);

    const updateMark = (rowIdx, subjectName, field, value) => {
        setRows((prev) => prev.map((row, ri) => {
            if (ri !== rowIdx) return row;
            // Prevent modification if already approved or published
            if (row.approvalStatus === "Approved" || isExamPublished) return row;
            return {
                ...row,
                marks: row.marks.map((m) => {
                    if (m.subjectName !== subjectName) return m;
                    if (field === "isAbsent") return { ...m, isAbsent: value, marksObtained: value ? 0 : m.marksObtained };
                    if (field === "marksObtained") return { ...m, marksObtained: Math.min(Number(value) || 0, m.maxMarks) };
                    return m;
                }),
            };
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const entries = rows
                .filter((row) => row.approvalStatus !== "Approved")
                .map((row) => ({
                    studentId: row.studentId,
                    remarks: row.remarks,
                    marks: row.marks
                        .filter((m) => teacherSet.has(m.subjectName))
                        .map((m) => ({ subjectName: m.subjectName, marksObtained: m.marksObtained, isAbsent: m.isAbsent })),
                }));

            if (entries.length === 0) {
                toast.error("All students are already approved and locked for editing.");
                setSaving(false);
                return;
            }

            const res = await portalService.saveTeacherResults(examId, entries);
            toast.success(res.message || "Marks saved.");
            // Refresh sheet
            const updated = await portalService.getTeacherResultSheet(examId);
            setSheet(updated);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading-center"><div className="spinner" />Loading result sheet…</div>;

    if (error) return (
        <div>
            <div className="page-header">
                <div><h1 className="page-title">Enter Marks</h1></div>
                <button className="btn btn-outline" onClick={() => navigate("/teacher/exams")}><FiArrowLeft size={14} /> Back</button>
            </div>
            <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "1.25rem", display: "flex", gap: "0.75rem" }}>
                <FiAlertCircle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                <div>
                    <div style={{ fontWeight: 700, color: "#dc2626" }}>Cannot open result sheet</div>
                    <div style={{ fontSize: 13, fontFamily: "monospace", marginTop: 4 }}>{error}</div>
                </div>
            </div>
        </div>
    );

    const subjects = exam?.subjects || [];

    return (
        <div>
            {/* Header */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">{exam?.title}</h1>
                    <p className="page-subtitle">
                        {exam?.examType} · {exam?.courseAdmitted} {exam?.courseYear}
                        {exam?.examDate ? ` · ${exam.examDate}` : ""}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/teacher/exams")}>
                        <FiArrowLeft size={14} /> Back
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving || allApproved || isExamPublished}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <FiSave size={14} /> {saving ? "Saving…" : allApproved ? "All Approved (Locked)" : "Save Marks"}
                    </button>
                </div>
            </div>

            {/* Legend & Notice */}
            {allApproved ? (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "0.85rem", fontSize: 13, color: "#166534", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiCheckCircle size={16} style={{ flexShrink: 0 }} />
                    <span>
                        <strong>Locked:</strong> All student results for this exam have been <strong>Approved by Admin</strong> (or published). Marks can no longer be modified by teachers.
                    </span>
                </div>
            ) : approvedCount > 0 ? (
                <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "0.85rem", fontSize: 13, color: "#1e40af", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiClock size={16} style={{ flexShrink: 0 }} />
                    <span>
                        <strong>Notice:</strong> {approvedCount} student result(s) are <strong>Approved & Locked</strong>. You can only edit and save marks for the remaining pending students.
                    </span>
                </div>
            ) : (
                <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.65rem 0.9rem", marginBottom: "0.85rem", fontSize: 12.5, color: "#1e40af", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiClock size={15} style={{ flexShrink: 0 }} />
                    <span>
                        <strong>Workflow Notice:</strong> Marks entered here will be saved under <strong>Draft / Pending Approval</strong>. Once an Administrator reviews and approves results, they will become locked for editing.
                    </span>
                </div>
            )}

            <div style={{ display: "flex", gap: "1rem", marginBottom: "0.85rem", flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 12.5 }}>
                    <FiEdit2 size={12} color="#16a34a" />
                    <span style={{ color: "#16a34a", fontWeight: 600 }}>Your subjects</span> — editable
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 12.5 }}>
                    <FiLock size={12} color="#9ca3af" />
                    <span style={{ color: "#9ca3af" }}>Other subjects / Approved</span> — read-only (locked)
                </div>
                {sheet?.teacherSubjects?.length > 0 && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginLeft: "auto" }}>
                        Your subjects: {sheet.teacherSubjects.join(", ")}
                    </div>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>No active students found for this class.</div></div>
            ) : (
                <div className="card">
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                            <thead>
                                <tr style={{ background: "#f8fafc", borderBottom: "2px solid var(--border)" }}>
                                    <th style={{ padding: "0.6rem 0.75rem", textAlign: "left", fontWeight: 700, whiteSpace: "nowrap", minWidth: 160 }}>Student</th>
                                    {subjects.map((sub) => {
                                        const mine = teacherSet.has(sub.name);
                                        return (
                                            <th key={sub.name} style={{ padding: "0.6rem 0.5rem", textAlign: "center", minWidth: 110, whiteSpace: "nowrap" }}>
                                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                                    <span style={{ color: mine ? "#15803d" : "#9ca3af", fontWeight: mine ? 700 : 500 }}>{sub.name}</span>
                                                    <span style={{ fontSize: 10.5, color: "var(--text-muted)" }}>/{sub.maxMarks} (pass: {sub.passingMarks})</span>
                                                    {mine ? <FiEdit2 size={10} color="#15803d" /> : <FiLock size={10} color="#9ca3af" />}
                                                </div>
                                            </th>
                                        );
                                    })}
                                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "center", minWidth: 70, whiteSpace: "nowrap" }}>Total</th>
                                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "center", minWidth: 55, whiteSpace: "nowrap" }}>%</th>
                                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "center", minWidth: 45, whiteSpace: "nowrap" }}>Grade</th>
                                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "center", minWidth: 80, whiteSpace: "nowrap" }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, ri) => {
                                    const isRowApproved = row.approvalStatus === "Approved" || isExamPublished;
                                    const allMarks = subjects.map((sub) => {
                                        const m = row.marks.find((m) => m.subjectName === sub.name);
                                        return m || { marksObtained: 0, isAbsent: false, maxMarks: sub.maxMarks };
                                    });
                                    const totalMax = allMarks.reduce((s, m) => s + m.maxMarks, 0);
                                    const totalObt = allMarks.reduce((s, m) => s + (m.isAbsent ? 0 : m.marksObtained), 0);
                                    const pct = totalMax > 0 ? ((totalObt / totalMax) * 100).toFixed(1) : "—";

                                    return (
                                        <tr key={row.studentId} style={{ borderBottom: "1px solid var(--border)", background: isRowApproved ? "#fcfdfd" : "#fff" }}>
                                            {/* Student */}
                                            <td style={{ padding: "0.55rem 0.75rem" }}>
                                                <div style={{ fontWeight: 600 }}>{row.fullName}</div>
                                                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{row.enrollmentNo}</div>
                                            </td>

                                            {/* Subject columns */}
                                            {subjects.map((sub) => {
                                                const mine = teacherSet.has(sub.name);
                                                const m = row.marks.find((m) => m.subjectName === sub.name);
                                                if (!m) return <td key={sub.name} style={{ padding: "0.5rem", textAlign: "center", color: "var(--text-muted)" }}>—</td>;

                                                if (mine && !isRowApproved) {
                                                    return (
                                                        <td key={sub.name} style={{ padding: "0.4rem 0.5rem", background: "#f0fdf4" }}>
                                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                                                {/* Absent checkbox */}
                                                                <label style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer", color: m.isAbsent ? "#dc2626" : "var(--text-muted)" }}>
                                                                    <input type="checkbox" checked={m.isAbsent} onChange={(e) => updateMark(ri, sub.name, "isAbsent", e.target.checked)} />
                                                                    AB
                                                                </label>
                                                                {/* Marks input */}
                                                                <input
                                                                    type="number" min="0" max={sub.maxMarks}
                                                                    disabled={m.isAbsent}
                                                                    value={m.isAbsent ? "" : m.marksObtained}
                                                                    onChange={(e) => updateMark(ri, sub.name, "marksObtained", e.target.value)}
                                                                    style={{
                                                                        width: 60, textAlign: "center", fontSize: 13, fontWeight: 700,
                                                                        padding: "3px 6px", border: `1px solid ${m.marksObtained < sub.passingMarks && !m.isAbsent ? "#fca5a5" : "#d1d5db"}`,
                                                                        borderRadius: 5, background: m.isAbsent ? "#f3f4f6" : "#fff",
                                                                        color: m.marksObtained < sub.passingMarks && !m.isAbsent ? "#dc2626" : "var(--text)",
                                                                    }}
                                                                />
                                                            </div>
                                                        </td>
                                                    );
                                                }

                                                // Read-only (either not teacher's subject OR row is approved)
                                                return (
                                                    <td key={sub.name} style={{ padding: "0.5rem", textAlign: "center", background: isRowApproved ? "#f8fafc" : "#fafafa" }}>
                                                        {m.isAbsent ? (
                                                            <span style={{ fontSize: 11.5, color: "#9ca3af", fontStyle: "italic" }}>AB</span>
                                                        ) : (
                                                            <span style={{ fontSize: 13, color: isRowApproved ? "#334155" : "#9ca3af", fontWeight: isRowApproved ? 600 : 400 }}>{m.marksObtained || "—"}</span>
                                                        )}
                                                    </td>
                                                );
                                            })}

                                            {/* Totals */}
                                            <td style={{ textAlign: "center", fontWeight: 700, fontSize: 13 }}>{totalObt}/{totalMax}</td>
                                            <td style={{ textAlign: "center", fontSize: 13 }}>{pct}%</td>
                                            <td style={{ textAlign: "center" }}>
                                                <span style={{ fontWeight: 800, fontSize: 14, color: GRADE_COLORS[row.grade] || "#6b7280" }}>
                                                    {row.grade || "—"}
                                                </span>
                                            </td>

                                            {/* Status */}
                                            <td style={{ textAlign: "center" }}>
                                                {isRowApproved ? (
                                                    <span style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", padding: "2px 7px", borderRadius: 99, fontSize: 10.5, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
                                                        <FiLock size={10} /> Approved
                                                    </span>
                                                ) : (
                                                    <span style={{ background: "#fef9c3", color: "#a16207", border: "1px solid #fde047", padding: "2px 7px", borderRadius: 99, fontSize: 10.5, fontWeight: 700 }}>
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Save footer */}
                    <div style={{ padding: "0.75rem 1rem", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: "0.5rem", alignItems: "center" }}>
                        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                            {rows.length} student{rows.length !== 1 ? "s" : ""} · {allApproved ? "All results locked" : "Saving marks only for pending subjects"}
                        </span>
                        <button className="btn btn-primary" onClick={handleSave} disabled={saving || allApproved || isExamPublished}
                            style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiSave size={13} /> {saving ? "Saving…" : allApproved ? "Locked" : "Save Marks"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// TeacherHomework — homework assigned by the logged-in teacher
// (mirrors TeacherExams — auto-scoped server-side via linkedEmployee)
// ─────────────────────────────────────────────────────────────────────────────
export function TeacherHomework() {
    const { academicYear } = useApp();
    const navigate = useNavigate();
    const [homeworkList, setHomeworkList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        portalService.getTeacherHomework()
            .then((result) => setHomeworkList(extract(result) || []))
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading homework…</div>;

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">My Homework</h1>
                    <p className="page-subtitle">
                        {homeworkList.length} assignment{homeworkList.length !== 1 ? "s" : ""} · {academicYear?.label}
                    </p>
                </div>
                <button className="btn btn-primary" onClick={() => navigate("/homework/new")}>
                    Assign Homework
                </button>
            </div>

            {error && (
                <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#7f1d1d" }}>{error}</div>
            )}

            {homeworkList.length === 0 && !error ? (
                <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                    <FiBookOpen size={40} style={{ opacity: 0.2, display: "block", margin: "0 auto 0.75rem" }} />
                    <p>No homework assigned yet.</p>
                </div></div>
            ) : (
                <div style={{ display: "grid", gap: "0.75rem" }}>
                    {homeworkList.map((hw) => {
                        const overdue = new Date(hw.dueDate) < new Date();
                        const pct = hw.totalStudents ? Math.round((hw.submittedCount / hw.totalStudents) * 100) : 0;
                        return (
                            <div key={hw._id} className="card" style={{ padding: "1rem 1.25rem", borderLeft: `4px solid ${overdue ? "#dc2626" : "#7c3aed"}` }}>
                                <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: 4 }}>
                                            <span style={{ fontWeight: 700, fontSize: 14 }}>{hw.title}</span>
                                            {overdue && <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>Overdue</span>}
                                        </div>
                                        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 6 }}>
                                            {hw.subject?.name} · {hw.course?.shortName || hw.course?.courseName || "Course"} {hw.yearNumber ? `— ${hw.yearNumber} Year` : ""} · Due {hw.dueDate?.slice(0, 10)}
                                        </div>
                                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                            {hw.submittedCount}/{hw.totalStudents} submitted ({pct}%) · {hw.gradedCount} graded
                                        </div>
                                    </div>
                                    <button className="btn btn-primary btn-sm" onClick={() => navigate(`/teacher/homework/${hw._id}/submissions`)}
                                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                                        <FiEye size={12} /> Submissions
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TeacherHomeworkSubmissions — class roster + inline grading for one homework
// (mirrors TeacherResultEntry's marks-table pattern)
// ─────────────────────────────────────────────────────────────────────────────
export function TeacherHomeworkSubmissions() {
    const { homeworkId } = useParams();
    const navigate = useNavigate();

    const [entries, setEntries] = useState([]);
    const [homeworkTitle, setHomeworkTitle] = useState("");
    const [maxMarks, setMaxMarks] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [gradingId, setGradingId] = useState(null); // student _id currently being graded
    const [marksDraft, setMarksDraft] = useState("");
    const [feedbackDraft, setFeedbackDraft] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            portalService.getHomeworkSubmissions(homeworkId),
            portalService.getTeacherHomework(), // for title/maxMarks — getSubmissionsByHomework returns a bare roster array, not homework metadata
        ])
            .then(([subResult, hwListResult]) => {
                setEntries(extract(subResult) || []);
                const hwList = extract(hwListResult) || [];
                const hw = hwList.find((h) => h._id === homeworkId);
                setHomeworkTitle(hw?.title || "");
                setMaxMarks(hw?.maxMarks || 0);
            })
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, [homeworkId]);

    useEffect(() => { load(); }, [load]);

    const startGrading = (entry) => {
        setGradingId(entry.student._id);
        setMarksDraft(entry.submission?.marksObtained ?? "");
        setFeedbackDraft(entry.submission?.feedback ?? "");
    };

    const saveGrade = async (entry) => {
        if (marksDraft === "" || Number(marksDraft) < 0) {
            toast.error("Enter valid marks");
            return;
        }
        if (maxMarks && Number(marksDraft) > maxMarks) {
            toast.error(`Marks cannot exceed ${maxMarks}`);
            return;
        }
        setSaving(true);
        try {
            await portalService.gradeHomeworkSubmission(entry.submission._id, {
                marksObtained: Number(marksDraft),
                feedback: feedbackDraft,
            });
            toast.success("Graded successfully");
            setGradingId(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading-center"><div className="spinner" />Loading submissions…</div>;

    const submittedCount = entries.filter((e) => e.status !== "Pending").length;
    const gradedCount = entries.filter((e) => e.status === "Graded").length;

    return (
        <div>
            <div className="page-header">
                <button className="btn btn-outline" onClick={() => navigate("/teacher/homework")} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                    <FiArrowLeft size={13} /> Back
                </button>
                <div>
                    <h1 className="page-title">{homeworkTitle || "Submissions"}</h1>
                    <p className="page-subtitle">
                        {entries.length} students · {submittedCount} submitted · {gradedCount} graded
                    </p>
                </div>
            </div>

            {error && (
                <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#7f1d1d" }}>{error}</div>
            )}

            <div className="card">
                <div className="table-wrapper">
                    <table>
                        <thead>
                            <tr>
                                <th>Student</th>
                                <th>Status</th>
                                <th>Submitted On</th>
                                <th style={{ textAlign: "center" }}>Marks</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry) => {
                                const isGradingThis = gradingId === entry.student._id;
                                return (
                                    <tr key={entry.student._id}>
                                        <td>
                                            <div style={{ fontWeight: 600, fontSize: 13 }}>{entry.student.fullName}</div>
                                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{entry.student.rollNo}</div>
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: 11.5, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
                                                background: entry.status === "Graded" ? "#dcfce7" : entry.status === "Pending" ? "#f1f5f9" : entry.status === "Late" ? "#fef3c7" : "#e8f1f8",
                                                color: entry.status === "Graded" ? "#15803d" : entry.status === "Pending" ? "#475569" : entry.status === "Late" ? "#92400e" : "#002747",
                                            }}>{entry.status}</span>
                                        </td>
                                        <td style={{ fontSize: 12.5 }}>
                                            {entry.submission?.submittedAt ? new Date(entry.submission.submittedAt).toLocaleString() : "—"}
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                            {isGradingThis ? (
                                                <input type="number" min={0} max={maxMarks || undefined} value={marksDraft}
                                                    onChange={(e) => setMarksDraft(e.target.value)}
                                                    className="form-control" style={{ width: 70, textAlign: "center", display: "inline-block" }} />
                                            ) : (
                                                entry.submission?.marksObtained ?? "—"
                                            )}
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            {entry.status === "Pending" ? (
                                                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Not submitted</span>
                                            ) : isGradingThis ? (
                                                <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end", alignItems: "center" }}>
                                                    <input value={feedbackDraft} onChange={(e) => setFeedbackDraft(e.target.value)}
                                                        placeholder="Feedback (optional)" className="form-control" style={{ width: 160, fontSize: 12 }} />
                                                    <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => saveGrade(entry)}>
                                                        <FiSave size={12} />
                                                    </button>
                                                    <button className="btn btn-outline btn-sm" onClick={() => setGradingId(null)}>Cancel</button>
                                                </div>
                                            ) : (
                                                <button className="btn btn-outline btn-sm" onClick={() => startGrading(entry)}
                                                    style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                                                    <FiEdit2 size={12} /> {entry.status === "Graded" ? "Regrade" : "Grade"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default TeacherDashboard;